import { CountryFlag, Select, SelectItem, SelectProps } from '@dashboard/ui'
import { useMemo } from 'react'
import { Country, UnitedStates } from '@/api/dashboard'
import { getCountries } from '@/translates'
import { useTranslation } from 'react-i18next'

const countries = new Set(Object.values(Country))

type Jurisdiction = {
  country: Country
  state?: UnitedStates
}

type SelectJurisdictionProps = Omit<SelectProps<Jurisdiction>, 'items' | 'placeholder' | 'search'> &
  Partial<Pick<SelectProps<Jurisdiction>, 'placeholder' | 'search'>>

export const SelectJurisdiction = ({ placeholder, search, ...rest }: SelectJurisdictionProps) => {
  const { t } = useTranslation()

  const items: SelectItem<Jurisdiction>[] = useMemo(() => {
    const usa = [
      {
        children: t('creator.terms.country.usa-default'),
        value: { country: Country.US },
        icon: () => <CountryFlag country={Country.US} />,
      },
      {
        children: t('creator.terms.country.usa-al'),
        value: { country: Country.US, state: UnitedStates.USAL },
        icon: () => <CountryFlag country={Country.US} />,
      },
      {
        children: t('creator.terms.country.usa-ms'),
        value: { country: Country.US, state: UnitedStates.USMS },
        icon: () => <CountryFlag country={Country.US} />,
      },
      {
        children: t('creator.terms.country.usa-ne'),
        value: { country: Country.US, state: UnitedStates.USNE },
        icon: () => <CountryFlag country={Country.US} />,
      },
      {
        children: t('creator.terms.country.usa-pr'),
        value: { country: Country.US, state: UnitedStates.USPR },
        icon: () => <CountryFlag country={Country.US} />,
      },
    ]
    const others = Object.entries(getCountries())
      .filter(([value]) => value != Country.US && countries.has(value as Country))
      .map(([value, children]) => ({
        children: children as string,
        value: { country: value } as Jurisdiction,
        icon: () => <CountryFlag country={value} />,
      }))

    return [...usa, ...others]
  }, [t])

  return (
    <Select<Jurisdiction>
      {...rest}
      placeholder={placeholder || t('select-country')}
      search={
        search || {
          placeholder: t('SearchInputPlaceHolder'),
          fn: (search, { children }) => {
            const s = search.toLowerCase().trim()
            const text = children as string
            return text.toLowerCase().trim().includes(s)
          },
        }
      }
      items={items}
      determine={v => `${v.country}:${v.state || 'default'}`}
    />
  )
}
